/**
 * Which filters each page offers. The filter bar shows these groups, the
 * client sends only their filters, and the API ignores any other filter on
 * the page's request, so a filter chosen on one page never changes another
 * page's numbers without being visible there (audit MET-02).
 */

/** Filter groups → request filter keys. */
export const FILTER_GROUPS = {
  platform: ["platform"],
  // Audience: the reader's profile.
  profile: ["specialty", "profession", "city", "state", "country"],
  utm: ["source", "medium", "campaign"],
  // Content: the item's own speciality tags (plan A9), independent of who reads it.
  content: ["contentSpecialty"],
  // Format: one content type, on pages that mix formats (mockup "Format" pill).
  format: ["contentType"],
};

/** Which filter groups make sense on which page: platform | profile | utm | content | format. */
export function filtersFor(dashboard, tab) {
  // The user page shows one person's own data: only the date range applies.
  if (dashboard === "user") return [];
  // Flags are about raw behaviour; profile filters would hide the very users to look at.
  if (dashboard === "monitoring") return [];
  if (dashboard === "acquisition" && ["web", "app", "android", "ios"].includes(tab)) return ["profile"];
  if (dashboard === "acquisition" && tab === "funnel") return ["profile", "utm"];
  if (dashboard === "engagement" && ["loyalty"].includes(tab)) return [];
  // Speciality count compares audience and content specialities itself: no content filter there.
  if (dashboard === "engagement" && tab === "speciality") return ["platform", "profile"];
  // Pages that mix formats can narrow to one; a format's own page is that format already.
  if (dashboard === "manage" || (dashboard === "engagement" && ["overview", "behaviour"].includes(tab))) return ["platform", "profile", "content", "format"];
  // Content pages can also narrow to items with a content speciality.
  if (dashboard === "engagement") return ["platform", "profile", "content"];
  return ["platform", "profile"];
}

/** Request filter keys a page uses. */
export function filterKeysFor(dashboard, tab) {
  return filtersFor(dashboard, tab).flatMap((g) => FILTER_GROUPS[g]);
}

/** Every user-facing filter key (each belongs to one group). */
export const ALL_FILTER_KEYS = Object.values(FILTER_GROUPS).flat();

/**
 * `filters` with every user-facing filter the page does not offer blanked.
 * Keys outside the groups (e.g. contentType, set by the server) are kept.
 */
export function keepPageFilters(filters, dashboard, tab) {
  const keep = new Set(filterKeysFor(dashboard, tab));
  return Object.fromEntries(Object.entries(filters).map(([k, v]) => [k, ALL_FILTER_KEYS.includes(k) && !keep.has(k) ? "" : v]));
}
